/* eslint-disable better-mutation/no-mutation */

import { createSlice } from '@reduxjs/toolkit';
import getInitialState from './initial-state';
import utils from './utils';
import {
    deleteShipment,
    createShipmentCheckout,
    getShippingOptions,
    updateShipment,
    markAsBooked,
    markAsShipped,
    getItemWithoutShipment,
    getPaymentOptions,
    createShipment,
    getShippingConfirmation,
    getUnbookedShipments,
} from './actions';

const shippingCheckoutSlice = createSlice({
    name: 'shippingCheckout',
    initialState: getInitialState(),
    reducers: {
        openNonIntegratedModal: (state, { payload }) => {
            state.nonIntegrated.providerId = payload;
            state.nonIntegrated.open = true;
        },
        closeNonIntegratedModal: (state) => {
            state.nonIntegrated.providerId = null;
            state.nonIntegrated.open = false;
            state.nonIntegrated.markedAsBooked = false;
            state.nonIntegrated.markedAsShipped = false;
            state.nonIntegrated.hasError = false;
            state.nonIntegrated.isUpdating = false;
        },
        setSelectedPayment: (state, { payload }) => {
            state.payment.selectedPayment = payload;
            state.payment.paymentMethodData = null;
            state.checkoutSummary.hasError = false;
        },
        setPaymentMethodData: (state, { payload }) => {
            state.payment.paymentMethodData = payload;
        },
        setCheckoutSummaryFetching: (state, { payload }) => {
            state.checkoutSummary.isFetching = payload;
            state.checkoutSummary.hasError = false;
            state.isUpdating = payload;
        },
        setCheckoutSummaryHasError: (state, { payload }) => {
            state.checkoutSummary.hasError = payload;
            state.checkoutSummary.isFetching = false;
            state.isUpdating = false;
        },
        openPaymentModal: (state, { payload }) => {
            const { open, paymentAttemptId } = payload;
            state.payment.modal = {
                open,
                paymentAttemptId,
            };
        },
        closePaymentModal: (state) => {
            state.payment.modal = {
                open: '',
            };
        },
        setTermsAccepted: (state, { payload }) => {
            state.checkoutSummary.termsAccepted = payload;
        },
        resetOngoingPaymentAttempt: (state) => {
            state.ongoingPaymentAttempt = {
                shipmentPaymentAttemptId: null,
                paymentMethod: null,
            };
        },
        setUnbookedShipments: (state, { payload }) => {
            state.unbookedShipments = {
                ...state.unbookedShipments,
                shipments: payload,
            };
        },
    },
    extraReducers: (builder) => {
        // create
        builder.addCase(
            createShipmentCheckout.fulfilled,
            (state, { payload }) => {
                state.shipments = payload.shipments;
                state.payment = {
                    ...state.payment,
                    shipmentCheckoutId: payload.shipmentCheckoutId,
                };
                state.sellerPhoneNumber = payload.sellerPhone;
                state.ongoingPaymentAttempt = {
                    ...state.ongoingPaymentAttempt,
                    ...payload.ongoingPaymentAttempt,
                };

                state.isFetching = false;
                state.hasFetchError = false;

                const totalShippingCost = payload.shipments.reduce(
                    (memo, item) => memo + item.shippingPrice,
                    0,
                );

                state.checkoutSummary.totalCost = totalShippingCost;
            },
        );
        builder.addCase(createShipmentCheckout.pending, (state) => {
            state.isInitialized = true;
            state.isFetching = true;
            state.hasFetchError = false;
        });
        builder.addCase(createShipmentCheckout.rejected, (state) => {
            state.isFetching = false;
            state.hasFetchError = true;
        });

        // delete
        builder.addCase(deleteShipment.fulfilled, (state, { payload }) => {
            const { shipmentCheckoutId, shipmentId } = payload;

            state.payment = {
                ...state.payment,
                shipmentCheckoutId: shipmentCheckoutId,
            };
            state.shipments = state.shipments.filter(
                (shipment) => shipment.shipmentId !== shipmentId,
            );
            state.hasDeleteError = false;
            state.isDeleting = false;

            const totalShippingCost = state.shipments.reduce(
                (memo, item) => memo + item.shippingPrice,
                0,
            );

            state.checkoutSummary.totalCost = totalShippingCost;
        });
        builder.addCase(deleteShipment.pending, (state) => {
            state.hasDeleteError = false;
            state.isDeleting = true;
        });
        builder.addCase(deleteShipment.rejected, (state) => {
            state.hasDeleteError = true;
            state.isDeleting = false;
        });

        // update
        builder.addCase(
            updateShipment.fulfilled,
            (state, { meta, payload }) => {
                const { arg } = meta;
                const { streetName, ...cleanedBuyerAddress } = arg.buyerAddress;
                state.shipments = state.shipments.map((shipment) =>
                    shipment.shipmentId === arg.shipmentId
                        ? {
                              ...shipment,
                              receiver: {
                                  ...shipment.receiver,
                                  ...cleanedBuyerAddress,
                                  street: arg.buyerAddress.streetName,
                              },
                              mostPreciseErrorKey: null,
                              isUpdatingShipment: false,
                          }
                        : shipment,
                );

                state.createdShipmentCheckoutId = payload.shipmentCheckoutId;
                state.error = null;
                state.isUpdating = false;
            },
        );
        builder.addCase(updateShipment.pending, (state, { meta }) => {
            const { arg } = meta;
            state.shipments = state.shipments.map((shipment) =>
                shipment.shipmentId === arg.shipmentId
                    ? {
                          ...shipment,
                          isUpdatingShipment: true,
                          mostPreciseErrorKey: null,
                      }
                    : shipment,
            );

            state.isUpdating = true;
        });
        builder.addCase(updateShipment.rejected, (state, { meta, payload }) => {
            const { arg } = meta;
            state.shipments = state.shipments.map((shipment) =>
                shipment.shipmentId === arg.shipmentId
                    ? {
                          ...shipment,
                          isUpdatingShipment: false,
                          mostPreciseErrorKey: payload.mostPreciseErrorKey,
                      }
                    : shipment,
            );

            state.error = {
                translationKey: payload.mostPreciseErrorKey,
            };
            state.isUpdating = false;
        });

        // create shipment
        builder.addCase(
            createShipment.fulfilled,
            (state, { meta, payload }) => {
                const { arg } = meta;

                const { selectedShipping } = state.itemWithoutShipment;
                state.itemWithoutShipment = {
                    ...state.itemWithoutShipment,
                    selectedShipping: {
                        providerId:
                            arg.providerId || selectedShipping.providerId,
                        productId: arg.productId || selectedShipping.productId,
                    },
                };

                state.createdShipmentCheckoutId = payload.shipmentCheckoutId;
                state.error = null;
                state.isCreating = false;
                state.isCreated = true;
            },
        );

        builder.addCase(createShipment.pending, (state) => {
            state.isCreating = true;
            state.isCreateInitialized = true;
        });

        builder.addCase(createShipment.rejected, (state, { meta, payload }) => {
            const { arg } = meta;

            const { selectedShipping } = state.itemWithoutShipment;
            state.itemWithoutShipment = {
                ...state.itemWithoutShipment,
                selectedShipping: {
                    providerId: arg.providerId || selectedShipping?.providerId,
                    productId: arg.productId || selectedShipping?.productId,
                },
            };

            state.error = {
                translationKey: payload?.mostPreciseErrorKey,
                hasUnSupportedProduct: payload?.hasUnSupportedProduct,
            };
            state.isCreating = false;
        });

        // get shipping info
        builder.addCase(
            getItemWithoutShipment.fulfilled,
            (state, { payload }) => {
                state.itemWithoutShipment = {
                    ...payload,
                    isInitialized: true,
                    isFetching: false,
                    error: null,
                };
            },
        );
        builder.addCase(getItemWithoutShipment.pending, (state) => {
            state.itemWithoutShipment = {
                isInitialized: true,
                isFetching: true,
                error: null,
            };
        });
        builder.addCase(
            getItemWithoutShipment.rejected,
            (state, { payload }) => {
                state.itemWithoutShipment = {
                    isInitialized: true,
                    isFetching: false,
                    shipmentCheckoutId: payload.shipmentCheckoutId,
                    error: {
                        errorCode: payload?.errorCode,
                    },
                };
            },
        );

        // get shipping options
        builder.addCase(getShippingOptions.fulfilled, (state, { payload }) => {
            const {
                data,
                bringEnabled,
                postnordEnabled,
                dhlEnabled,
                schenkerEnabled,
            } = payload;
            //Decorate products with cost & uuid for shipping dialog:
            const productsDecorated = data.productsPerWeightSpan.map(
                (weightClass) => {
                    const productsWithDecoration = weightClass.products.map(
                        (product) => {
                            let outage = false;
                            switch (product.providerId) {
                                case 'BringDenmark':
                                    outage = !bringEnabled;
                                    break;
                                case 'PostNordParcel':
                                    outage = !postnordEnabled;
                                    break;
                                case 'SchenkerPrivpak':
                                    outage = !schenkerEnabled;
                                    break;
                                case 'DHL':
                                    outage = !dhlEnabled;
                                    break;
                                default:
                                    outage = false;
                                    break;
                            }

                            return {
                                ...product,
                                productId: product.id,
                                cost: product.price,
                                uuid: utils.getUuid(),
                                outage: outage,
                            };
                        },
                    );

                    const filteredProductsWithDecoration =
                        productsWithDecoration.filter(
                            (shippingProvider) =>
                                shippingProvider.providerId !==
                                    'BringDenmark' || bringEnabled,
                        );
                    return {
                        ...weightClass,
                        products: filteredProductsWithDecoration,
                    };
                },
            );
            state.shipping.shippingOptions = productsDecorated;

            state.shipping.isShippingFetching = false;
            state.shipping.hasShippingError = false;
        });
        builder.addCase(getShippingOptions.pending, (state) => {
            state.shipping.isShippingFetching = true;
            state.shipping.hasShippingError = false;
            state.shipping.isShippingInitialized = true;
        });
        builder.addCase(getShippingOptions.rejected, (state) => {
            state.shipping.isShippingFetching = false;
            state.shipping.hasShippingError = true;
        });

        // mark as booked
        builder.addCase(markAsBooked.fulfilled, (state) => {
            state.nonIntegrated.markedAsBooked = true;
            state.nonIntegrated.hasError = false;
            state.nonIntegrated.isUpdating = false;
        });
        builder.addCase(markAsBooked.pending, (state) => {
            state.nonIntegrated.markedAsBooked = true;
            state.nonIntegrated.hasError = false;
            state.nonIntegrated.isUpdating = true;
        });
        builder.addCase(markAsBooked.rejected, (state) => {
            state.nonIntegrated.markedAsBooked = false;
            state.nonIntegrated.hasError = true;
            state.nonIntegrated.isUpdating = false;
        });

        // mark as shipped
        builder.addCase(markAsShipped.fulfilled, (state) => {
            state.nonIntegrated.markedAsShipped = true;
            state.nonIntegrated.hasError = false;
            state.nonIntegrated.isUpdating = false;
        });
        builder.addCase(markAsShipped.pending, (state) => {
            state.nonIntegrated.markedAsShipped = true;
            state.nonIntegrated.hasError = false;
            state.nonIntegrated.isUpdating = true;
        });
        builder.addCase(markAsShipped.rejected, (state) => {
            state.nonIntegrated.markedAsShipped = false;
            state.nonIntegrated.hasError = true;
            state.nonIntegrated.isUpdating = false;
        });

        // get payment options
        builder.addCase(getPaymentOptions.fulfilled, (state, { payload }) => {
            const { paymentOptions } = payload;
            state.payment = {
                ...state.payment,
                paymentOptions: paymentOptions,
            };
        });
        builder.addCase(getPaymentOptions.pending, (state) => {
            state.payment = {
                ...state.payment,
                isFetching: true,
                hasError: null,
            };
        });
        builder.addCase(getPaymentOptions.rejected, (state) => {
            state.payment = {
                ...state.payment,
                isFetching: false,
                hasError: true,
            };
        });
        // Get shipping confirmation
        builder.addCase(
            getShippingConfirmation.fulfilled,
            (state, { payload }) => {
                state.shippingConfirmation.data = payload;
                state.shippingConfirmation.isFetching = false;
                state.shippingConfirmation.hasError = false;
            },
        );
        builder.addCase(getShippingConfirmation.pending, (state) => {
            state.shippingConfirmation.isInitialized = true;
            state.shippingConfirmation.isFetching = true;
            state.shippingConfirmation.hasError = false;
        });
        builder.addCase(
            getShippingConfirmation.rejected,
            (state, { payload }) => {
                state.shippingConfirmation.isFetching = false;

                if (payload.isLongPolling) {
                    state.shippingConfirmation.isLongPolling =
                        payload.isLongPolling;
                } else {
                    state.shippingConfirmation.hasError = true;
                }
            },
        );
        // get unbooked shipments
        builder.addCase(
            getUnbookedShipments.fulfilled,
            (state, { payload }) => {
                const { unbookedShipments } = payload;
                state.unbookedShipments = {
                    ...state.unbookedShipments,
                    shipments: unbookedShipments,
                    loading: false,
                    error: false,
                };
            },
        );
        builder.addCase(getUnbookedShipments.pending, (state) => {
            state.unbookedShipments = {
                ...state.unbookedShipments,
                loading: true,
                error: false,
            };
        });
        builder.addCase(getUnbookedShipments.rejected, (state) => {
            state.unbookedShipments = {
                ...state.unbookedShipments,
                loading: false,
                error: true,
            };
        });
    },
});

export const {
    openNonIntegratedModal,
    closeNonIntegratedModal,
    setSelectedPayment,
    setPaymentMethodData,
    setCheckoutSummaryFetching,
    setCheckoutSummaryHasError,
    openPaymentModal,
    closePaymentModal,
    setTermsAccepted,
    resetOngoingPaymentAttempt,
    setUnbookedShipments,
} = shippingCheckoutSlice.actions;

export default shippingCheckoutSlice.reducer;
